/* Atelier — product.js : galerie et plein écran, variantes, formulaire d'ajout, délai de livraison, barre d'achat mobile.
   Chargé uniquement sur la fiche produit. */

(() => {
  if (customElements.get('product-info')) return;
  const theme = window.theme || {};

  const formatMoney = (cents, format = theme.moneyFormat || '{{amount_with_comma_separator}} €') => {
    const value = Number(cents) / 100;
    const fmt = (decimals, thousands, decimal) => {
      const [int, dec] = value.toFixed(decimals).split('.');
      const intFmt = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? `${intFmt}${decimal}${dec}` : intFmt;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return fmt(2, ',', '.');
        case 'amount_no_decimals': return fmt(0, ',', '.');
        case 'amount_with_comma_separator': return fmt(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(0, '.', ',');
        case 'amount_with_space_separator': return fmt(2, ' ', ',');
        case 'amount_with_apostrophe_separator': return fmt(2, "'", '.');
        default: return fmt(2, ' ', ',');
      }
    });
  };

  const scrollBehavior = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

  /* Ordre visuel d'une piste : l'élément « --lead » (visuel de la variante demandée) est affiché en premier (order: -1) */
  const visualOrder = (track, leadClass) => {
    const all = [...track.children];
    const lead = all.find((li) => li.classList.contains(leadClass));
    return lead ? [lead, ...all.filter((li) => li !== lead)] : all;
  };

  /* ---------- Conteneur produit : galerie (glisser, flèches, miniatures, plein écran) ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery]');
      if (this.track) {
        this.items = visualOrder(this.track, 'gallery-track__item--lead');
        this.counter = this.querySelector('[data-gallery-current]');
        this.thumbs = [...this.querySelectorAll('[data-thumb]')];
        this.thumbRow = this.querySelector('[data-thumbs]');
        this.steps = [...this.querySelectorAll('[data-gallery-step]')];
        this.zoomButton = this.querySelector('[data-zoom-open]');
        this.lightbox = this.querySelector('product-lightbox');
        this.index = 0;
        this.setActive(0, true);

        this.thumbs.forEach((btn) => btn.addEventListener('click', () => this.showMedia(btn.dataset.thumb)));
        this.steps.forEach((btn) => btn.addEventListener('click', () => {
          const next = this.items[this.index + Number(btn.dataset.galleryStep)];
          if (next) this.showMedia(next.dataset.mediaId);
        }));
        this.track.addEventListener('scroll', () => {
          if (this.raf) return;
          this.raf = requestAnimationFrame(() => {
            this.raf = null;
            this.setActive(Math.round(this.track.scrollLeft / this.track.clientWidth));
          });
        }, { passive: true });
        // Un clic (ou un appui sans glisser) sur l'image l'ouvre en plein écran ; le bouton loupe en est l'équivalent clavier
        this.track.addEventListener('click', (e) => {
          const item = e.target.closest('[data-zoomable]');
          if (item && this.lightbox && !e.target.closest('a, button, video, iframe')) this.lightbox.open(item.dataset.mediaId, this.zoomButton);
        });
        this.zoomButton?.addEventListener('click', () => {
          const item = this.items[this.index];
          if (item?.hasAttribute('data-zoomable')) this.lightbox?.open(item.dataset.mediaId, this.zoomButton);
        });
        this.addEventListener('lightbox:close', (e) => this.showMedia(e.detail.mediaId, 'auto'));
      }
      this.addEventListener('variant:change', (e) => this.onVariantChange(e.detail.variant, e.detail.mediaId));
      // Lien d'une carte de réassurance vers un accordéon : l'ouvre avant le défilement vers l'ancre
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-open-accordion]');
        const target = link && document.getElementById((link.getAttribute('href') || '').slice(1));
        if (target instanceof HTMLDetailsElement) target.open = true;
      });
    }

    setActive(index, force = false) {
      const max = this.items.length - 1;
      const i = Math.min(max, Math.max(0, index));
      if (i === this.index && !force) return;
      this.index = i;
      const item = this.items[i];
      if (!item) return;
      if (this.counter) this.counter.textContent = String(i + 1);
      this.steps.forEach((btn) => {
        btn.disabled = Number(btn.dataset.galleryStep) < 0 ? i === 0 : i === max;
      });
      if (this.zoomButton) this.zoomButton.hidden = !item.hasAttribute('data-zoomable');
      let current = null;
      this.thumbs.forEach((btn) => {
        const on = btn.dataset.thumb === item.dataset.mediaId;
        btn.setAttribute('aria-current', on ? 'true' : 'false');
        if (on) current = btn.parentElement;
      });
      // Miniature active ramenée dans la rangée (défilement de la rangée seule, jamais de la page)
      if (current && this.thumbRow && this.thumbRow.scrollWidth > this.thumbRow.clientWidth) {
        const left = current.offsetLeft - (this.thumbRow.clientWidth - current.offsetWidth) / 2;
        this.thumbRow.scrollTo({ left, behavior: force ? 'auto' : scrollBehavior() });
      }
    }

    showMedia(mediaId, behavior = scrollBehavior()) {
      if (!mediaId || !this.track) return;
      const index = this.items.findIndex((li) => li.dataset.mediaId === String(mediaId));
      if (index < 0) return;
      this.track.scrollTo({ left: this.items[index].offsetLeft, behavior });
      this.setActive(index);
    }

    onVariantChange(variant, mediaId) {
      const sectionId = this.dataset.section;
      if (!variant) return;
      if (mediaId || variant.media_id) this.showMedia(mediaId || variant.media_id);

      const priceEl = document.getElementById(`Price-${sectionId}`)?.querySelector('[data-price]');
      if (priceEl) {
        const onSale = variant.compare_at_price > variant.price;
        priceEl.classList.toggle('price--on-sale', onSale);
        priceEl.innerHTML = onSale
          ? `<span class="price__sale">${formatMoney(variant.price)}</span> <s class="price__compare">${formatMoney(variant.compare_at_price)}</s>`
          : `<span class="price__regular">${formatMoney(variant.price)}</span>`;
      }

      const availability = this.querySelector('[data-availability]');
      if (availability) {
        availability.innerHTML = variant.available
          ? `<span class="availability__dot availability__dot--in"></span>${availability.dataset.inStock || ''}`
          : `<span class="availability__dot"></span>${theme.strings.soldOut}`;
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }
  customElements.define('product-info', ProductInfo);

  /* ---------- Plein écran (<dialog> natif) : glisser ou flèches, zoom au clic ou au bouton, Échap pour fermer ---------- */
  class ProductLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.closest('dialog');
      this.track = this.querySelector('[data-lightbox-track]');
      if (!this.dialog || !this.track) return;
      this.items = visualOrder(this.track, 'lightbox__slide--lead');
      this.counter = this.querySelector('[data-lightbox-current]');
      this.zoomButton = this.querySelector('[data-lightbox-zoom]');
      this.steps = [...this.querySelectorAll('[data-lightbox-step]')];
      this.index = 0;

      this.steps.forEach((btn) => btn.addEventListener('click', () => this.go(this.index + Number(btn.dataset.lightboxStep))));
      this.zoomButton?.addEventListener('click', () => this.setZoom(!this.zoomed));
      this.track.addEventListener('click', (e) => {
        if (e.target.closest('img')) this.setZoom(!this.zoomed, e);
      });
      this.track.addEventListener('scroll', () => {
        if (this.zoomed || this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          this.update(Math.round(this.track.scrollLeft / this.track.clientWidth));
        });
      }, { passive: true });
      this.dialog.addEventListener('keydown', (e) => {
        if (this.zoomed || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
        e.preventDefault();
        this.go(this.index + (e.key === 'ArrowRight' ? 1 : -1));
      });
      this.dialog.addEventListener('close', () => {
        this.setZoom(false);
        const item = this.items[this.index];
        if (item) this.dispatchEvent(new CustomEvent('lightbox:close', { bubbles: true, detail: { mediaId: item.dataset.mediaId } }));
        if (this.opener && document.contains(this.opener) && !this.opener.hidden) this.opener.focus();
      });
    }

    open(mediaId, opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener;
      this.dialog.showModal();
      const index = Math.max(0, this.items.findIndex((li) => li.dataset.mediaId === String(mediaId)));
      this.track.scrollTo({ left: this.items[index].offsetLeft, behavior: 'auto' });
      this.update(index);
    }

    go(index) {
      const item = this.items[index];
      if (!item) return;
      this.setZoom(false);
      this.track.scrollTo({ left: item.offsetLeft, behavior: scrollBehavior() });
      this.update(index);
    }

    update(index) {
      const max = this.items.length - 1;
      this.index = Math.min(max, Math.max(0, index));
      // Image affichée et ses voisines chargées tout de suite (lazy ailleurs : rien n'est téléchargé avant l'ouverture)
      this.items.slice(Math.max(0, this.index - 1), this.index + 2).forEach((li) => {
        const img = li.querySelector('img');
        if (img && img.loading !== 'eager') img.loading = 'eager';
      });
      if (this.counter) this.counter.textContent = String(this.index + 1);
      this.steps.forEach((btn) => {
        btn.disabled = Number(btn.dataset.lightboxStep) < 0 ? this.index === 0 : this.index === max;
      });
    }

    /* Zoom : l'image passe à 2,5 fois sa taille affichée dans une diapositive défilante (glisser pour se déplacer),
       centrée sur le point cliqué ; sizes est élargi pour charger une version plus nette. */
    setZoom(on, event) {
      const slide = this.items[this.index];
      const img = slide?.querySelector('img');
      if (!img) return;
      if (!on) {
        if (!this.zoomed) return;
        this.zoomed = false;
        slide.classList.remove('is-zoomed');
        this.track.classList.remove('is-zoomed');
        img.style.width = '';
        img.sizes = '100vw';
        this.zoomButton?.setAttribute('aria-pressed', 'false');
        this.track.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
        return;
      }
      // L'image remplit la diapositive en object-fit: contain : on retrouve la zone réellement peinte
      const box = img.getBoundingClientRect();
      const naturalW = img.naturalWidth || Number(img.getAttribute('width')) || 1;
      const naturalH = img.naturalHeight || Number(img.getAttribute('height')) || 1;
      const scale = Math.min(box.width / naturalW, box.height / naturalH);
      const shownW = naturalW * scale;
      const shownH = naturalH * scale;
      const clamp = (n) => Math.min(1, Math.max(0, n));
      const rx = event ? clamp((event.clientX - box.left - (box.width - shownW) / 2) / shownW) : 0.5;
      const ry = event ? clamp((event.clientY - box.top - (box.height - shownH) / 2) / shownH) : 0.5;
      const width = Math.round(shownW * 2.5);
      this.zoomed = true;
      img.sizes = `${width}px`;
      img.style.width = `${width}px`;
      slide.classList.add('is-zoomed');
      this.track.classList.add('is-zoomed');
      this.zoomButton?.setAttribute('aria-pressed', 'true');
      slide.scrollTo({
        left: rx * slide.scrollWidth - slide.clientWidth / 2,
        top: ry * slide.scrollHeight - slide.clientHeight / 2,
        behavior: 'auto'
      });
    }
  }
  customElements.define('product-lightbox', ProductLightbox);

  /* ---------- Sélecteur de variantes ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
      this.addEventListener('change', () => this.onChange());
      this.addEventListener('toggle', (e) => this.onSupportToggle(e.target), true);
      this.updateAvailability(this.selectedOptions());
    }

    /* Accordéon des supports. Un format dans un <details> fermé n'est pas focalisable, et le clavier n'atteint
       que le bouton coché d'un groupe radio : ouvrir un support y coche donc son premier format disponible,
       pour que le groupe ouvert contienne toujours la sélection (prix, image et Tab restent cohérents). */
    onSupportToggle(group) {
      if (!group.open || !group.matches?.('details[data-support]')) return;
      // Repli pour les navigateurs sans <details name> : refermer les autres supports de la même option
      group.parentElement.querySelectorAll(':scope > details[data-support][open]').forEach((d) => {
        if (d !== group) d.open = false;
      });
      if (group.querySelector('input:checked')) return;
      const inputs = [...group.querySelectorAll('input[type="radio"]')];
      const input = inputs.find((i) => !group.querySelector(`label[for="${i.id}"]`)?.classList.contains('is-unavailable')) || inputs[0];
      if (!input) return;
      input.checked = true;
      this.onChange();
    }

    selectedOptions() {
      return [...this.querySelectorAll('fieldset')].map((fs) => fs.querySelector('input:checked')?.value);
    }

    onChange() {
      const options = this.selectedOptions();
      let variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
      this.querySelectorAll('[data-selected-value]').forEach((el) => {
        el.textContent = options[Number(el.dataset.selectedValue)] || '';
      });
      this.updateAvailability(options);
      const info = this.closest('product-info');
      const form = document.getElementById(info?.dataset.form);
      const input = form?.querySelector('[data-variant-input]');
      if (input) input.value = variant ? variant.id : '';
      const button = form?.querySelector('[data-add-button]');
      const label = form?.querySelector('[data-add-label]');
      if (button && label) {
        const available = Boolean(variant && variant.available);
        button.disabled = !available;
        label.textContent = !variant ? theme.strings.unavailable : (available ? theme.strings.addToCart : theme.strings.soldOut);
      }
      document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant, formId: info?.dataset.form } }));
      // Variante sans visuel propre (affiche, toile) : visuel de son support dans l'accordéon
      const supportMedia = this.querySelector('details[data-support] input:checked')?.closest('[data-support-media]')?.dataset.supportMedia;
      if (variant) info?.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, mediaId: variant.media_id || supportMedia } }));
    }

    /* Grise les valeurs qui ne mènent à aucune variante disponible avec les autres options choisies */
    updateAvailability(selected) {
      this.querySelectorAll('fieldset').forEach((fs, index) => {
        fs.querySelectorAll('input').forEach((input) => {
          const candidate = [...selected];
          candidate[index] = input.value;
          const match = this.variants.find((v) => v.options.every((o, i) => o === candidate[i]));
          const available = Boolean(match && match.available);
          const label = fs.querySelector(`label[for="${input.id}"]`);
          label?.classList.toggle('is-unavailable', !available);
          const sr = label?.querySelector('[data-unavailable-label]');
          if (sr) sr.textContent = available ? '' : ` (${theme.strings.soldOut.toLowerCase()})`;
        });
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Formulaire d'ajout au panier ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-add-button]');
      this.error = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.querySelectorAll('[data-quantity] [data-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const input = this.querySelector('[data-quantity] input');
          const next = Math.min(99, Math.max(1, Number(input.value || 1) + Number(btn.dataset.step)));
          input.value = String(next);
        });
      });
    }

    async onSubmit(event) {
      if (!window.theme?.cart) return;
      event.preventDefault();
      if (this.button.disabled || this.button.getAttribute('aria-busy') === 'true') return;
      const id = Number(this.form.querySelector('[data-variant-input]').value);
      const quantity = Number(this.form.querySelector('[name="quantity"]')?.value || 1);
      if (!id) return;
      this.error.hidden = true;
      this.button.setAttribute('aria-busy', 'true');
      try {
        await window.theme.cart.add([{ id, quantity }]);
      } catch (e) {
        this.error.textContent = e.message || theme.strings.cartError;
        this.error.hidden = false;
      } finally {
        this.button.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Délai de livraison estimé (jours ouvrés, calculé côté client pour ne pas être figé par le cache) ----------
     Délai = [fabrication min, max, transport min, max] : celui de la variante choisie (métachamps de variante) s'il existe,
     sinon celui du produit (data-product-delay). */
  const toDelay = (d) => (Array.isArray(d) && d.length === 4 && d.every((n) => Number.isFinite(Number(n))) ? d.map(Number) : null);
  const parseDelay = (value) => {
    try {
      return toDelay(JSON.parse(value || 'null'));
    } catch (e) {
      return null;
    }
  };

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.text = this.querySelector('[data-delivery-text]');
      this.productDelay = parseDelay(this.dataset.productDelay);
      this.formId = this.closest('product-info')?.dataset.form;
      this.render(parseDelay(this.dataset.delay));
      // Après le premier rendu : seuls les changements dus au choix du format sont annoncés
      this.text?.setAttribute('aria-live', 'polite');
      this.onVariantChanged = (e) => {
        if (e.detail?.formId !== this.formId) return;
        this.render(toDelay(e.detail.variant?.delivery) || this.productDelay);
      };
      document.addEventListener('variant:changed', this.onVariantChanged);
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this.onVariantChanged);
    }

    render(delay) {
      const max = delay ? delay[1] + delay[3] : 0;
      this.hidden = !max;
      if (!max || !this.text) return;
      const min = delay[0] + delay[2];
      const addBusinessDays = (date, days) => {
        const result = new Date(date);
        let added = 0;
        while (added < days) {
          result.setDate(result.getDate() + 1);
          const day = result.getDay();
          if (day !== 0 && day !== 6) added += 1;
        }
        return result;
      };
      const fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'fr', { weekday: 'short', day: 'numeric', month: 'short' });
      // Date insécable : « ven. 16 oct. » ne se coupe pas en fin de ligne
      const date = (days) => fmt.format(addBusinessDays(new Date(), days)).replace(/\s/g, ' ');
      const from = date(min);
      const to = date(max);
      const template = this.dataset.template || 'Livraison estimée entre le {from} et le {to}';
      const next = template.replace('{from}', from).replace('{to}', to);
      if (this.text.textContent !== next) this.text.textContent = next;
    }
  }
  customElements.define('delivery-estimate', DeliveryEstimate);

  /* ---------- Barre d'achat collante (mobile) : visible dès l'arrivée tant que le bouton principal n'est pas à l'écran
     (au-dessous comme au-dessus : sur mobile il est à près de deux écrans du haut), masquée quand il est visible et au
     niveau du pied de page ; support et format choisis, prix, état du bouton ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      if (!this.form) return;
      this.hidden = false;
      const submit = this.form.querySelector('[data-add-button]');
      this.querySelector('[data-sticky-submit]')?.addEventListener('click', () => {
        if (submit && !submit.disabled) this.form.requestSubmit(submit);
      });
      let buttonVisible = true;
      let footerVisible = false;
      const toggle = () => {
        const visible = !buttonVisible && !footerVisible;
        this.classList.toggle('is-visible', visible);
        this.inert = !visible;
      };
      toggle();
      // Observation du bouton lui-même, sans écouteur de scroll : la barre ne double jamais un bouton déjà visible.
      new IntersectionObserver(([entry]) => {
        buttonVisible = entry.isIntersecting;
        toggle();
      }).observe(submit || this.form);
      const footer = document.querySelector('footer');
      if (footer) {
        new IntersectionObserver(([entry]) => {
          footerVisible = entry.isIntersecting;
          toggle();
        }).observe(footer);
      }
      document.addEventListener('variant:changed', (e) => {
        if (e.detail.formId !== this.dataset.form) return;
        const v = e.detail.variant;
        const available = Boolean(v && v.available);
        const button = this.querySelector('[data-sticky-submit]');
        const label = this.querySelector('[data-sticky-label]');
        const price = this.querySelector('[data-sticky-price]');
        const text = this.querySelector('[data-sticky-variant]');
        if (button) button.disabled = !available;
        if (label) label.textContent = !v ? theme.strings.unavailable : (available ? theme.strings.addToCart : theme.strings.soldOut);
        if (price && v) price.textContent = formatMoney(v.price);
        if (text && v) text.textContent = v.options.join(' / ');
      });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Recommandations (chargées à l'approche, API native) ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url) return;
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        this.load();
      }, { rootMargin: '0px 0px 400px 0px' });
      observer.observe(this);
    }
    async load() {
      try {
        const res = await fetch(this.dataset.url);
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
        else this.remove();
      } catch (e) { /* silencieux : section optionnelle */ }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);
})();
